import BackendService from 'BackendService';
import { useCallback, type Dispatch, type SetStateAction } from 'react';
import type { AdminFundUpdateEntry } from '../../models/FundUpdateEntry';
import type { FundUpdatesAdminState } from './fundUpdatesAdmin.types';

type SetAdminState = Dispatch<SetStateAction<FundUpdatesAdminState>>;

function withTitle(
  entries: AdminFundUpdateEntry[],
  externalId: string,
  title: string | null,
): AdminFundUpdateEntry[] {
  return entries.map((entry) => (entry.externalId === externalId ? { ...entry, title } : entry));
}

function setEntryTitle(setState: SetAdminState, externalId: string, title: string | null) {
  setState((prev) => ({
    ...prev,
    drafts: withTitle(prev.drafts, externalId, title),
    published: withTitle(prev.published, externalId, title),
  }));
}

/**
 * Saves an update's optional title. Shown at once and saved straight away — on a
 * published update that means it is live immediately (it is not part of the
 * draft/publish content). Rolls back and reports an error if the save fails.
 */
export function useFundUpdateTitle(fundOpportunityId: string, setState: SetAdminState) {
  const handleTitleChange = useCallback(
    async (update: AdminFundUpdateEntry, title: string) => {
      const previous = update.title ?? null;
      const next = title.trim() || null;
      if (next === previous) return;
      setEntryTitle(setState, update.externalId, next);
      try {
        await new BackendService().setFundUpdateTitle(fundOpportunityId, update.externalId, next);
      } catch (err) {
        console.error('[FundUpdatesAdminPanel] setFundUpdateTitle failed:', err);
        setEntryTitle(setState, update.externalId, previous);
        setState((prev) => ({
          ...prev,
          error: 'Unable to save the update title. Please try again.',
        }));
      }
    },
    [fundOpportunityId, setState],
  );

  return { handleTitleChange };
}
